import { useFormContext, useWatch } from "react-hook-form";
import { Pencil, UserRound } from "lucide-react";
import { OfferForm } from "./offer.schema";
import { useStepsStore } from "./state";
import { useOffersStore } from "@/src/stores/offersStore";

/**
 * Builder header (prototype v2): the client and currency live here — visible
 * on every step, changed via dialogs — instead of occupying steps of their
 * own. "Change" reopens the client dialog; "Offer details" the description.
 */
export function BuilderHeader({ offerId }: { offerId?: string }) {
  const { control, setValue } = useFormContext<OfferForm>();
  const steps = useStepsStore();
  const { currencies } = useOffersStore();
  const customer = useWatch({ control, name: "customer" });
  const description = useWatch({ control, name: "description" });

  const clientName = [customer?.firstName, customer?.lastName].filter(Boolean).join(" ");
  const context = [
    description?.charterType,
    description?.enquiryDate ? new Date(description.enquiryDate).toLocaleDateString("en-GB", { month: "short", year: "numeric" }) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-wrap items-center gap-3 justify-between bg-white border border-blue-100 rounded-lg px-4 py-2.5">
      <button
        type="button"
        onClick={() => steps.setStep("Customer")}
        className="flex items-center gap-2.5 text-left group"
      >
        <span className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
          {clientName ? (
            <span className="text-xs font-semibold text-blue-700">
              {customer?.firstName?.[0]}
              {customer?.lastName?.[0]}
            </span>
          ) : (
            <UserRound size={15} className="text-blue-600" />
          )}
        </span>
        <span className="min-w-0">
          <span className="flex items-center gap-1.5 font-semibold text-sm">
            {clientName || "Select a client"}
            <Pencil size={12} className="text-blue-500 opacity-0 group-hover:opacity-100" />
          </span>
          {context && <span className="block text-xs text-slate-500 truncate">{context}</span>}
        </span>
        <span className="text-blue-600 text-sm font-medium ml-1">{clientName ? "Change" : ""}</span>
      </button>
      <div className="flex items-center gap-2">
        <select
          className="border border-blue-100 rounded-md text-sm py-1.5 px-2 bg-white"
          value={description?.currency ?? "EUR"}
          onChange={(e) => setValue("description.currency", e.target.value, { shouldDirty: true })}
        >
          {(currencies ?? []).map((c: any) => (
            <option key={c.code} value={c.code}>
              {c.symbol ?? ""} {c.code}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => steps.setStep("Description")}
          className="text-sm text-blue-600 font-medium border border-blue-100 rounded-md py-1.5 px-3 hover:bg-blue-50"
        >
          Offer details
        </button>
      </div>
    </div>
  );
}
